import { Injectable } from '@angular/core';
import { EupRoutesService } from '@core/eupRoutes.service';
import { Observable, of, from } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { Consts } from '@shared/consts';
import { DownloadNotificationService } from '@shared/downloadNotification/downloadNotification.service';
import { LogoutParameters } from './models/logout-parameters';
import { SpinnerService } from '@core/spinner/spinner.service';
import { ShellContextService } from '../shell-context/shell-context.service';
import { StickyHeaderService } from '../stickyHeaderService/stickyHeader.service';
import { PendoService } from '@shared/pendo/pendo.service';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { TimberService } from '@logging/timber.service';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { AuthService } from './auth.service';
import { AuthCookie, CookieReadWriteService } from './cookie-read-write.service';
import { AppConfigService } from '../appConfig/appConfigService';
import { APP_ID } from '../../../assets/consts';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { PlatformLoginService } from '../platform-login/platform-login.service';
import { PlatformLoginStateService } from '../platform-login/platform-login-state.service';
import { AuthAngularService } from '@itero/auth-angular';
import { CookieService } from '@core/cookie.service';

@Injectable({
	providedIn: 'root',
})
export class LogoutService {
	constructor(
		private eupRoutesService: EupRoutesService,
		private globalSettingsService: GlobalSettingsService,
		private downloadNotificationService: DownloadNotificationService,
		private spinnerService: SpinnerService,
		private shellContextService: ShellContextService,
		private stickyHeaderService: StickyHeaderService,
		private pendoService: PendoService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private softwareOptionsService: SoftwareOptionsService,
		private timberService: TimberService,
		private authService: AuthService,
		private cookiesService: CookieReadWriteService,
		private appConfigService: AppConfigService,
		private readonly httpService: HttpClient,
		private readonly platformLoginService: PlatformLoginService,
		private readonly platformLoginStateService: PlatformLoginStateService,
		private readonly authAngularService: AuthAngularService,
		private readonly cookieService: CookieService
	) {}

	logoutClient(params: LogoutParameters = {}) {
		this.timberService.info(`logout client called. logoutUrl: ${params.logoutUrl}`, { module: 'LogoutService' });

		const userId = this.globalSettingsService.get()?.contactId;
		const isNewLoginFlow = this.platformLoginStateService.isActive();

		try {
			this.authService.authStatus$.next({
				isAuthenticated: false,
				isUnauthenticatedByTimeout: params.byTimeout,
				userId,
			});

			this.resetState();
		} finally {
			if (isNewLoginFlow) {
				// New login flow: always redirect to Decision Maker login page
				this.platformLoginService.login();
			} else {
				const redirectUrl = this.getLogoutUrl(params);

				if (redirectUrl) {
					this.redirect(redirectUrl);
				}
			}
		}
	}

	logout(sessionId: string = '', params: LogoutParameters = {}): Observable<boolean> {
		this.timberService.info(`logout called. sessionId: ${sessionId}`, { module: 'LogoutService' });

		const { missing, cookie: sessionInfo } = this.cookiesService.authCookie;

		if (missing?.length > 0) {
			this.timberService.error(`logout called. missing cookies: ${JSON.stringify(missing)}`, { module: 'AuthService' });
		}

		const isNewLoginFlow = this.platformLoginStateService.isActive();

		return (isNewLoginFlow ? from(this.performPlatformLoginLogout(sessionInfo)) : of(null)).pipe(
			catchError(() => of(null)),
			switchMap(() => this.logoutSession(sessionInfo)),
			switchMap(res => {
				if (res.logoutUrl) {
					params.logoutUrl = res.logoutUrl;
				}

				const invalidate$ =
					sessionInfo &&
					typeof sessionInfo.sessionId === 'string' &&
					sessionInfo.sessionId.trim() !== '' &&
					(!sessionId || (sessionId && sessionInfo.sessionId.trim() === sessionId.trim()))
						? this.authService.invalidateSession(sessionInfo.sessionId)
						: of(false);

				this.timberService.info('logout new flow. invalidate', { module: 'LogoutService' });

				return invalidate$.pipe(
					catchError(() => of(false)),
					tap(() => this.logoutClient(params))
				);
			})
		);
	}

	logoutWithoutSessionInvalidation(params: LogoutParameters = {}): false {
		const midcUrl = this.eupRoutesService.midcUrl;

		if (!midcUrl) {
			this.timberService.error('Could not get midc url. App initialization failed', {
				module: 'LogoutService',
			});

			return false;
		}

		const isNewLoginFlow = this.platformLoginStateService.isActive();

		// Persist the deep-link target across the login redirect for the callback to forward to.
		if (params?.returnUrl) {
			this.cookieService.set(Consts.CookiesNames.RETURNURL, params.returnUrl, Number.MAX_SAFE_INTEGER);
		}

		this.resetState();

		if (isNewLoginFlow) {
			this.platformLoginService.login();
		} else {
			this.redirect(this.loginUrl(midcUrl, params?.returnUrl));
		}

		return false;
	}

	// New flow: re-authenticate and return the user to their current page (deep link).
	relogin(returnUrl: string): void {
		const loginEmail = this.globalSettingsService.get()?.loginEmail ?? '';

		this.timberService.info(`relogin called. returnUrl: ${returnUrl}`, { module: 'LogoutService' });

		try {
			this.resetState();
			this.platformLoginService.relogin(returnUrl, loginEmail);
		} catch (error) {
			this.timberService.error('Error during relogin, falling back to platform login', {
				module: 'LogoutService',
				error: error instanceof Error ? error : new Error(String(error)),
			});
			this.platformLoginService.login();
		}
	}

	redirect(url: string) {
		window.location.href = url;
	}

	private logoutSession(sessionInfo: AuthCookie): Observable<any> {
		const midcUrl = this.eupRoutesService.midcUrl;

		if (!midcUrl || !sessionInfo) {
			this.timberService.error(`Could not get midc url. Session info: ${JSON.stringify(sessionInfo)}`, {
				module: 'LogoutService',
			});

			return of(false);
		}

		this.timberService.info(`logout called. redirectUrl: ${midcUrl}`, { module: 'LogoutService' });

		const url = `${this.eupRoutesService.iTeroWebAuthApiUrl.replace(/\/auth\/?$/, '')}/${Consts.LogoutPath}`;
		const body = {
			sessionId: sessionInfo.sessionId,
			appId: APP_ID,
			redirectUri: this.loginUrl(midcUrl),
			idToken: sessionInfo.idToken,
		};

		return this.httpService.post(url, body, { withCredentials: true }).pipe(
			catchError((err: HttpErrorResponse) => {
				this.timberService.error(`Unable to logout. Session id: ${sessionInfo?.sessionId}, err ${err?.message}`, {
					module: 'LogoutService',
				});

				return of(false);
			})
		);
	}

	private resetState() {
		this.shellContextService.resetContext();
		this.stickyHeaderService.resetLogo();
		this.pendoService.closePendoInstance();
		this.featuresToggleSettingsService.resetFeaturesTogglesSync();
		this.softwareOptionsService.resetCompanySoftwareOptions();
		this.globalSettingsService.clear();
		this.downloadNotificationService.clear();
		this.spinnerService.stop();
		this.platformLoginStateService.clear();
		// Reset the auth-angular interceptor/service so a stale token isn't reused on the next login.
		this.authAngularService.ngOnDestroy();

		// clear cookies
		this.cookiesService.clearAuthCookie();
		this.cookiesService.clearBusinessCookie();
		this.cookiesService.clearInfraCookie();

		localStorage.clear();
		sessionStorage.clear();
	}

	private getLogoutUrl(params: LogoutParameters) {
		if (params.logoutUrl && params.logoutUrl.trim() !== '') {
			this.timberService.info(`logout client. redirect to logoutUrl: ${params?.logoutUrl}`, { module: 'LogoutService' });

			return params.logoutUrl;
		} else {
			this.timberService.info(`logout client. redirect to logoutUrl: ${params?.logoutUrl} failed.`, { module: 'LogoutService' });

			return this.loginUrl(this.eupRoutesService.midcUrl);
		}
	}

	private loginUrl(baseUrl: string, returnUrl?: string) {
		const normalizedBase = baseUrl.replace(/\/+$/, '');
		const normalizedReturnPath = returnUrl ? '/' + returnUrl.replace(/\/+$/, '') : '';

		return `${normalizedBase}/login${normalizedReturnPath}`;
	}

	private async performPlatformLoginLogout(sessionInfo: AuthCookie): Promise<void> {
		const username = this.globalSettingsService.get()?.loginEmail;

		if (!sessionInfo?.idToken || !sessionInfo?.sessionId || !username) {
			this.timberService.warn('Missing idToken, sessionId, or username for platform login logout', { module: 'LogoutService' });

			return;
		}

		await this.platformLoginService.logout(sessionInfo.idToken, sessionInfo.sessionId, username);
	}
}
